import {
  GiftSubDetails,
  GiftSubDetailsAndRecipient,
  GiftSubDetailsProps,
  GiftSubRecipientAddress,
  GiftSubRecipientAddressProps,
  GiftSubConfirmation,
  GiftSubConfirmationProps,
  MultiStepFormFrame,
  PrepaidSubReviewDetails,
  OrderTranslations,
  useScreenSizeContext,
} from "@bluebottlecoffee/design-system/components";
import { useCart } from "@chordcommerce/react-autonomy";
import { loadStripe } from "@stripe/stripe-js";
import { useMachine } from "@xstate/react";
import { useRouter } from "next/router";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  AddressValidationCopy,
  recipientAddressValidation,
} from "../../lib/utils/recipient-address-validation";
import { AriaTranslations } from "../../lib/sanity/shared";
import { trimAndReorderDateString } from "../../lib/utils";
import { GiftSubscriptionsCopy } from "../../lib/sanity/gift-subscription-queries";
import {
  Step,
  giftSubCheckoutFormStateMachine,
} from "./gift-subscription-checkout-form-state-machine";
import { ProductWithShortDescription } from "../../lib/transformers/conversion";
import { Dialect } from "../../lib/utils/locale";
import { isFeatureEnabled } from "../../lib/utils/is-feature-enabled";

type StepWithButtonText<T> = T & {
  disclaimer?: string;
  previousCTAText?: string;
  nextCTAText: string;
};

type MultiStepFormComponents = {
  [Property in Step]: JSX.Element;
};

export type CheckoutFlowVariant = "default" | "revamp";

export type GiftSubscriptionCheckoutFormProps = {
  checkoutFlowVariant?: CheckoutFlowVariant;
  giftSubConfirmation: StepWithButtonText<
    Omit<GiftSubConfirmationProps, "purchaseFlowDetails">
  >;
  giftSubDetails: StepWithButtonText<
    Omit<GiftSubDetailsProps, "onDetailChange" | "product"> & {
      product: ProductWithShortDescription;
    }
  >;
  giftSubRecipientAddress: StepWithButtonText<
    Omit<GiftSubRecipientAddressProps, "onRecipientInfoChange">
  >;
  copy: Pick<GiftSubscriptionsCopy, "back"> &
    Pick<OrderTranslations, "immediately"> &
    Pick<AriaTranslations, "liveRegionLoadingText"> &
    AddressValidationCopy;
  renderStartDate: boolean;
  region: Dialect["region"];
  lang: Dialect["lang"];
};

export const GiftSubscriptionCheckoutForm: FunctionComponent<
  GiftSubscriptionCheckoutFormProps
> = ({
  checkoutFlowVariant = "default",
  copy,
  giftSubConfirmation,
  giftSubDetails,
  giftSubRecipientAddress,
  renderStartDate,
  region,
  lang,
}) => {
  const { product } = giftSubDetails;
  const { isSmallViewPort } = useScreenSizeContext();
  const isRevampCheckout = checkoutFlowVariant === "revamp";
  // Desktop revamp: details + recipient in one step, then review (2 UI steps).
  // Mobile revamp keeps the original 3-step flow.
  const useCombinedView = isRevampCheckout && !isSmallViewPort();

  const {
    isRequired,
    inputTooLong,
    emailFormatInvalid,
    usPostalCodeFormatInvalid,
    countryCodeFormatInvalid,
    giftNoteTooLong,
  } = copy;

  const addressValidationCopy: AddressValidationCopy = {
    isRequired,
    inputTooLong,
    emailFormatInvalid,
    usPostalCodeFormatInvalid,
    countryCodeFormatInvalid,
    giftNoteTooLong,
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Set loading false at render to account for using the browser back button from checkout
  useEffect(() => {
    setLoading(false);
  }, []);

  const machineId = "gift-subscription-checkout-form";

  const giftSubCheckoutFormMachine = useMemo(
    () =>
      giftSubCheckoutFormStateMachine({
        ctas: {
          stepOne: giftSubDetails.nextCTAText,
          stepTwo: giftSubRecipientAddress.nextCTAText,
          stepThree: giftSubConfirmation.nextCTAText,
        },
        disclaimers: {
          stepOne: giftSubDetails.disclaimer,
          stepTwo: giftSubRecipientAddress.disclaimer,
          stepThree: giftSubConfirmation.disclaimer,
        },
        id: machineId,
        product,
      }),
    [],
  );

  const [state, send] = useMachine(giftSubCheckoutFormMachine);

  const { context, meta, value: step } = state;

  const {
    index,
    disclaimer,
    nextButtonText: machineNextButtonText,
  } = meta[`${machineId}.${step}`];

  const { stepOne, stepTwo } = context.data;

  const {
    actionableDate: actDate,
    computedSubtotal,
    duration,
    installmentCount,
    interval,
    lineItems,
    subscriptionOptionId,
    discount,
  } = stepOne;

  const actionableDate: string | undefined = renderStartDate
    ? actDate
    : undefined;

  const { address, email, giftNote } = stepTwo;

  const {
    name,
    street,
    extended,
    locality,
    district,
    postalCode,
    countryCode,
  } = address;

  const { editFormProps, ...giftSubRecipientAddressProps } =
    giftSubRecipientAddress;

  const variant = product.variants.find(
    (productVariant) => productVariant.sku === lineItems[0].sku,
  );

  const { purchasePrepaidSubscription } = useCart();

  const router = useRouter();

  const checkout = async () => {
    const { sku, quantity } = lineItems[0];
    setLoading(true);
    try {
      // remove after checkout is fully migrated to embedded checkout
      const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);
      const stripe = await stripePromise;
      const order = await purchasePrepaidSubscription({
        // request MUST be in this order to succeed
        lineItem: {
          // lineItem.quantity MUST be 1
          // whereas options.prepaidSubscription.lineItems[i].quantity
          // should be the actual quantity being purchased
          quantity: 1,
          sku,
          options: {
            prepaidSubscription: {
              // set time to midday to buffer for utc offset in North America
              actionableDate: actionableDate
                ? `${actionableDate}T12:00:00`
                : new Date(),
              installmentCount,
              interval: {
                length: interval.length, // length MUST come before units for request to succeed
                units: interval.unit,
              },
              gift: true,
              giftNote,
              autoRedeem: true,
              recipient: {
                name,
                email,
              },
              lineItems: [
                {
                  sku,
                  quantity,
                },
              ],
              shipAddress: {
                // shipAddress MUST be in this order to succeed
                name,
                address1: street,
                address2: extended,
                city: locality,
                countryIso: countryCode,
                stateName: district,
                zipcode: postalCode,
              },
            },
          },
        },
      });
      if (isFeatureEnabled(process.env.NEXT_PUBLIC_ENABLE_EMBEDDED_CHECKOUT)) {
        router.push(`/${region}/${lang}/checkout`);
      } else {
        stripe.redirectToCheckout({
          sessionId: order.checkoutSessionId,
        });
      }
    } catch (error) {
      setErrorMessage(error.message);
      setLoading(false);
    }
  };

  const purchaseFlowDetails: PrepaidSubReviewDetails = {
    ...(actionableDate && { actionableDate }),
    computedSubtotal,
    discount,
    duration,
    installmentCount,
    interval,
    lineItems,
    recipientInfo: {
      address,
      email,
      giftNote,
    },
    variant: {
      name: product.name,
      description: product.shortDescription,
      image: {
        altText: "", // not needed here (non-descriptive image)
        src: variant?.images[0]?.desktop.src ?? product.images[0].desktop.src,
      },
      optionsText: variant.optionValues[0].presentation,
    },
  };

  const giftSubDetailsCommonProps = {
    ...giftSubDetails,
    onDetailChange: (detail) => {
      send({
        type: "UPDATE",
        value: { stepOne: detail },
      });
    },
    detailsSelected: {
      ...(actionableDate && { actionableDate }),
      durationId: duration._id,
      intervalId: interval._id,
      optionId: subscriptionOptionId,
      variantSku: variant.sku,
    },
    ...(isRevampCheckout && { variant: "revamp" as const }),
  };

  const giftSubRecipientAddressCommonProps = {
    ...giftSubRecipientAddressProps,
    editFormProps: { ...editFormProps, address },
    email,
    giftNote,
    onRecipientInfoChange: (info) => {
      send({
        type: "UPDATE",
        value: { stepTwo: info },
      });
    },
    ...(isRevampCheckout && { variant: "revamp" as const }),
  };

  const totalSteps = Object.keys(giftSubCheckoutFormMachine.states).length;
  const displayTotalSteps = useCombinedView ? 2 : totalSteps;
  const combinedViewStep = step === "stepThree" ? 2 : 1;
  const displayActiveStep = useCombinedView ? combinedViewStep : index;

  function disclaimerData(): string | undefined {
    if (step === "stepThree") {
      if (actionableDate) {
        return trimAndReorderDateString(actionableDate);
      }
      return copy.immediately;
    }
    return undefined;
  }

  function onBack() {
    setErrorMessage(undefined);
    if (useCombinedView && step === "stepThree") {
      send("PREV_TO_DETAILS");
      return;
    }
    send("PREV");
  }

  function onNext() {
    setErrorMessage(undefined);
    if (index === 3) {
      checkout();
      return;
    }
    if (useCombinedView && step === "stepOne") {
      const stepTwoValidationResult = recipientAddressValidation(
        stepTwo,
        addressValidationCopy,
        region,
      );
      if (stepTwoValidationResult.isValid) {
        send("NEXT_TO_REVIEW");
      } else {
        setErrorMessage(stepTwoValidationResult.firstError);
      }
      return;
    }
    if (index === 2) {
      const stepTwoValidationResult = recipientAddressValidation(
        stepTwo,
        addressValidationCopy,
        region,
      );
      if (stepTwoValidationResult.isValid) {
        send("NEXT");
      } else {
        setErrorMessage(stepTwoValidationResult.firstError);
      }
      return;
    }
    send("NEXT");
  }

  const showBackButton = useCombinedView ? step === "stepThree" : index > 1;

  const disclaimerText: string | undefined =
    step === "stepOne" && renderStartDate ? undefined : disclaimer;

  const nextButtonText =
    useCombinedView && step === "stepOne"
      ? giftSubRecipientAddress.nextCTAText
      : machineNextButtonText;

  const hideButtons =
    useCombinedView && (step === "stepOne" || step === "stepThree");
  const frameErrorMessage = hideButtons ? undefined : errorMessage;
  const frameDisclaimer = hideButtons ? undefined : disclaimerText;
  const frameDisclaimerData = hideButtons ? undefined : disclaimerData();

  const confirmationActions = useCombinedView
    ? {
        backButtonText: copy.back,
        disclaimer: disclaimerText,
        disclaimerData: disclaimerData(),
        errorMessage,
        nextButtonLoadingText: loading ? copy.liveRegionLoadingText : undefined,
        nextButtonText: machineNextButtonText,
        showBackButton,
        onBack,
      }
    : undefined;

  const components: MultiStepFormComponents = {
    stepOne: useCombinedView ? (
      <GiftSubDetailsAndRecipient
        backButtonText={copy.back}
        disclaimer={disclaimerText}
        disclaimerData={disclaimerData()}
        errorMessage={errorMessage}
        giftSubDetails={giftSubDetailsCommonProps}
        giftSubRecipientAddress={giftSubRecipientAddressCommonProps}
        nextButtonLoadingText={loading ? copy.liveRegionLoadingText : undefined}
        nextButtonText={giftSubRecipientAddress.nextCTAText}
        showBackButton={false}
        onBack={onBack}
      />
    ) : (
      <GiftSubDetails {...giftSubDetailsCommonProps} />
    ),
    stepTwo: (
      <GiftSubRecipientAddress {...giftSubRecipientAddressCommonProps} />
    ),
    stepThree: (
      <GiftSubConfirmation
        {...giftSubConfirmation}
        actions={confirmationActions}
        purchaseFlowDetails={purchaseFlowDetails}
        {...(isRevampCheckout && { variant: "revamp" as const })}
      />
    ),
  };

  let contentClassName: string | undefined;
  if (isRevampCheckout && !useCombinedView) {
    contentClassName =
      "box-border flex w-full max-w-[375px] flex-col items-start gap-6 p-0";
  } else if (isRevampCheckout && useCombinedView) {
    contentClassName =
      "box-border flex w-full max-w-[814px] flex-col items-start gap-4 px-0 pb-0 pt-0";
  }

  return (
    <MultiStepFormFrame
      activeStep={displayActiveStep}
      backButtonText={copy.back}
      contentClassName={contentClassName}
      disclaimer={frameDisclaimer}
      disclaimerData={frameDisclaimerData}
      errorMessage={frameErrorMessage}
      hideButtons={hideButtons}
      nextButtonLoadingText={loading ? copy.liveRegionLoadingText : undefined}
      nextButtonText={nextButtonText}
      negTopMargin
      onBack={onBack}
      onNext={onNext}
      showBackButton={showBackButton}
      totalSteps={displayTotalSteps}
      variant={isRevampCheckout ? "revamp" : "default"}
    >
      {components[`${step}`]}
    </MultiStepFormFrame>
  );
};
